import type { FirstProductLocationGuardState } from './firstProductLocationGuard';

/**
 * Existing-product reconciliation for the wizard's first-product step
 * (US3, T057; product-creation contract §8).
 *
 * The step is satisfied by existing data only when products for the verified
 * real current location fetched successfully AND the real-location total is
 * greater than zero. Loading, stale data, or a fetch error is never evidence
 * of zero products, and test-location products never satisfy the step.
 */

export type ExistingProductFetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | {
      status: 'success';
      /** Canonical total for the location the fetch actually ran against. */
      totalDocs: number;
      /** Location the fetch was issued for (explicit X-Location-ID). */
      locationId: string;
    }
  | { status: 'error' };

export type ExistingProductEvidence =
  | { kind: 'satisfied'; totalDocs: number; locationId: string }
  | { kind: 'no_products' }
  | {
      kind: 'indeterminate';
      reason:
        | 'location_not_ready'
        | 'fetch_pending'
        | 'fetch_error'
        | 'location_mismatch';
    };

export const resolveExistingProductEvidence = (
  guard: FirstProductLocationGuardState,
  fetchState: ExistingProductFetchState,
): ExistingProductEvidence => {
  if (guard.kind !== 'ready') {
    return { kind: 'indeterminate', reason: 'location_not_ready' };
  }
  switch (fetchState.status) {
    case 'idle':
    case 'loading':
      return { kind: 'indeterminate', reason: 'fetch_pending' };
    case 'error':
      return { kind: 'indeterminate', reason: 'fetch_error' };
    case 'success': {
      if (String(fetchState.locationId) !== guard.location.id) {
        // A fetch that ran against another location (e.g. the test location
        // before a switch) says nothing about the real location.
        return { kind: 'indeterminate', reason: 'location_mismatch' };
      }
      if (fetchState.totalDocs > 0) {
        return {
          kind: 'satisfied',
          totalDocs: fetchState.totalDocs,
          locationId: guard.location.id,
        };
      }
      return { kind: 'no_products' };
    }
  }
};
